import type { Key } from 'react-aria-components';

export type FlatRow<T> = {
  id: Key;
  row: T;
  level: number;
  ancestorLines: boolean[];
  hasChildren: boolean;
  isExpanded: boolean;
  isLastChild: boolean;
};

type Options<T> = {
  rows: T[];
  getRowId: (row: T) => Key;
  getChildRows?: (row: T) => T[] | undefined;
  expandedKeys: Set<Key>;
  sort: (rows: T[]) => T[];
};

export function flattenRows<T>({ rows, getRowId, getChildRows, expandedKeys, sort }: Options<T>): Array<FlatRow<T>> {
  const flattened: Array<FlatRow<T>> = [];

  const visit = (siblings: T[], level: number, ancestorLines: boolean[]) => {
    const sorted = sort(siblings);

    sorted.forEach((row, index) => {
      const id = getRowId(row);
      const children = getChildRows?.(row);
      const hasChildren = children !== undefined && children.length > 0;
      const isExpanded = hasChildren && expandedKeys.has(id);
      const isLastChild = index === sorted.length - 1;

      flattened.push({ id, row, level, ancestorLines, hasChildren, isExpanded, isLastChild });

      if (isExpanded && children) {
        visit(children, level + 1, [...ancestorLines, !isLastChild]);
      }
    });
  };

  visit(rows, 0, []);

  return flattened;
}
